// FileSelectorButton.tsx
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { generateUUID } from "../../../utils/Common/Constant";
import { AssessmentButton, showToast } from "ui-components";
import { useTranslation } from "react-i18next";
import { staffAugAddSetting } from "../../../service/Settings/Settings";
import { t } from "i18next";
import { uploadMutipleEncryptedFiles } from "../../../utils/Common/MediaUploader";

const MAX_FILE_COUNT = 100;
const MAX_FILE_SIZE_MB = 10 * 1024 * 1024; //10 MB

interface FileSelectorButtonProps {
  files: any[];
  setFiles: React.Dispatch<React.SetStateAction<any[]>>;
  fileProgressList: any[];
  setFileProgressList: React.Dispatch<React.SetStateAction<any[]>>;
  onFilesUploaded?: (allFiles: any[], acceptedFiles: File[]) => void;
  isChildrenUsed?: boolean;
}

const useFileSelectorUploader = ({
  files,
  setFiles,
  setFileProgressList,
  onFilesUploaded,
  isChildrenUsed,
  fileProgressList,
}: FileSelectorButtonProps) => {
  const { t } = useTranslation();
  const handleFileSelect = useCallback(
    async (acceptedFiles: File[], fileRejections: any[]) => {
      if (fileRejections.length > 0) {
        let errorMessages = "";
        for (const rejection of fileRejections) {
          for (const e of rejection.errors) {
            if (e.code === "file-invalid-type") {
              errorMessages = "Invalid file type selected.";
              break;
            } else {
              errorMessages += `${e.message}; `;
            }
          }
          if (errorMessages) break;
        }
        if (errorMessages) {
          if (isChildrenUsed) {
            showToast(
              `${t("SETTING.FILE_SIZE_MUST_BE_LESS_THAN")} ${
                MAX_FILE_SIZE_MB / 1024 / 1024
              } MB`,
              "error",
            );
          }
          console.error(errorMessages);
          return;
        }
      }

      const totalFiles = files.length + acceptedFiles.length;
      if (totalFiles > MAX_FILE_COUNT) {
        const msg = "Maximum file count exceeded.";
        if (isChildrenUsed) {
          console.error(msg);
        }
        console.error(msg);
        return;
      }

      const newFileProgressList = acceptedFiles.map((file, index) => ({
        id: `${Date.now()}-${index}`,
        file,
        progress: 0,
        status: "Uploading",
      }));
      setFileProgressList((prev: any) => [...prev, ...newFileProgressList]);

      const uploadedUrls: any[] = Array(acceptedFiles.length).fill(null);

      const uploadFile = async (file: File, index: number) => {
        try {
          const updateProgress = (percentage: number) => {
            setFileProgressList((prevList: any) =>
              prevList.map((item: any) =>
                item.file === file ? { ...item, progress: percentage } : item,
              ),
            );
          };

          // simulate upload progress
          for (let i = 0; i <= 100; i += 10) {
            await new Promise((resolve) => setTimeout(resolve, 50));
            updateProgress(i);
          }

          // actual upload
          const imageData = await uploadMutipleEncryptedFiles(
            file,
            // companyId/licenceDocuments/licenceId/master/generateUUID
            `master/documents/${generateUUID()}`,
          );

          if (imageData?.ImageUrl) {
            uploadedUrls[index] = imageData;
            setFileProgressList((prevList: any) =>
              prevList.map((item: any) =>
                item.file === file
                  ? {
                      ...item,
                      progress: 100,
                      status: "Completed",
                      imageUrl: imageData?.ImageUrl,
                    }
                  : item,
              ),
            );

            try {
              const payload = {
                stage: "masterDocuments",
                stageRequirements: {
                  title: file.name, // use actual file name
                  filePath: imageData.fileName,
                },
              };
              await staffAugAddSetting(payload);
            } catch (error) {
              console.error("Error saving uploaded file:", error);
            }
          }
        } catch (error) {
          console.error("Upload failed for:", file.name, error);
          setFileProgressList((prevList: any) =>
            prevList.map((item: any) =>
              item.file === file ? { ...item, status: "failed" } : item,
            ),
          );
        }
      };

      await Promise.all(
        acceptedFiles.map((file, index) => uploadFile(file, index)),
      );

      const validUploads = uploadedUrls.filter((url) => url);
      setFiles((prev: any) => [...prev, ...validUploads]);

      onFilesUploaded &&
        onFilesUploaded([...files, ...validUploads], acceptedFiles);
    },
    [
      files,
      setFiles,
      setFileProgressList,
      fileProgressList,
      onFilesUploaded,
      isChildrenUsed,
    ],
  );

  const { getInputProps, open } = useDropzone({
    onDrop: handleFileSelect,
    noClick: true,
    noKeyboard: true,
    multiple: true,
    maxSize: MAX_FILE_SIZE_MB,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "image/png": [".png"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
  });

  return { getInputProps, open };
};

const FileSelectorButton = (props: FileSelectorButtonProps) => {
  const { getInputProps, open } = useFileSelectorUploader(props);

  return (
    <div>
      <input {...getInputProps()} style={{ display: "none" }} />
      <AssessmentButton className={"lightBtn"} onClick={open}>
        {t("GENERAL.UPLOAD")}
      </AssessmentButton>
    </div>
  );
};

export default FileSelectorButton;
